/**
 * Arşiv sekmesi — personanın kişisel belge hafızası + yazım stili.
 *
 * İki farklı şey aynı ekranda çünkü ikisi de aynı yüklemeden beslenir:
 *   • `knowledge` / `procedure` / `dossier` bölümleri → klasik RAG. Belgenin
 *     İÇERİĞİ turda alıntılanır.
 *   • `style-exemplar` bölümü → stil parmak izi. Belgenin içeriği DEĞİL,
 *     yazım BİÇİMİ öğrenilir; retrieval'a hiç girmez.
 *
 * Bu ayrım UI'da açıkça anlatılır, çünkü kullanıcı "kendi yazdıklarımı
 * yükledim ama alıntılamıyor" ya da tersi "gizli evrakımı tartışmada
 * okudu" sürprizini yaşamamalı.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  COLLECTION_KIND_HINTS,
  COLLECTION_KIND_LABELS,
  RETRIEVAL_MODE_LABELS,
  STYLE_METRIC_LABELS,
  formatBytes,
  type CollectionDocument,
  type CollectionKind,
  type MemoryCollection,
  type RetrievalMode,
  type StyleProfile,
} from "../../domain/personaLibrary";
import {
  LibraryApiError,
  buildStyle,
  createCollection,
  deleteCollection,
  deleteCollectionDocument,
  documentDownloadUrl,
  listCollectionDocuments,
  listCollections,
  patchCollection,
  searchCollection,
  searchPersona,
  uploadCollectionDocument,
} from "../../transport/personaLibraryClient";
import { LibrarySearchPanel } from "./LibrarySearchPanel";
import {
  Btn,
  Chip,
  EmptyState,
  ErrorBanner,
  Field,
  Modal,
  SectionTitle,
  Select,
  TextArea,
  TextInput,
} from "./ui";

function DocumentRow({
  doc,
  onDelete,
  busy,
}: {
  doc: CollectionDocument;
  onDelete: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/[0.06] bg-black/20 px-2.5 py-1.5">
      <span className="text-[13px] opacity-60">📄</span>
      <span className="min-w-0 flex-1 truncate text-[12px] text-slate-300" title={doc.filename}>
        {doc.filename}
      </span>
      <span className="shrink-0 text-[10.5px] text-slate-600">{formatBytes(doc.byteSize)}</span>
      {doc.status === "ready" ? (
        <Chip tone="green">{doc.chunkCount} parça</Chip>
      ) : doc.status === "failed" ? (
        <Chip tone="rose" title={doc.error ?? undefined}>
          hata
        </Chip>
      ) : (
        <Chip tone="amber">işleniyor</Chip>
      )}
      {doc.status === "ready" && (
        <a
          href={documentDownloadUrl(doc.id)}
          target="_blank"
          rel="noreferrer"
          title={
            doc.hasOriginal
              ? "Orijinal dosyayı indir"
              : "Orijinal arşivde yok — dizinlenen metin indirilecek"
          }
          className="shrink-0 rounded p-1 text-slate-500 transition-colors hover:bg-violet-500/10 hover:text-violet-300"
          aria-label="Belgeyi indir"
        >
          ⬇
        </a>
      )}
      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        className="shrink-0 rounded p-1 text-slate-600 transition-colors hover:bg-rose-500/10 hover:text-rose-400 disabled:opacity-40"
        aria-label="Belgeyi sil"
      >
        ✕
      </button>
    </div>
  );
}

export function CollectionCard({
  collection,
  onChanged,
  onError,
}: {
  collection: MemoryCollection;
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [docs, setDocs] = useState<CollectionDocument[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadDocs = useCallback(async () => {
    try {
      setDocs(await listCollectionDocuments(collection.id));
    } catch (err) {
      onError(err instanceof LibraryApiError ? err.message : "Belgeler yüklenemedi.");
    }
  }, [collection.id, onError]);

  useEffect(() => {
    if (expanded) void loadDocs();
  }, [expanded, loadDocs]);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      // Sıralı yükleme: paralel gönderim extractor (:8903) ve embedder'ı
      // aynı anda doyurup 502'ye düşürüyor. Kullanıcı zaten dosya başına
      // ilerleme görüyor.
      for (const file of Array.from(files)) {
        setProgress(0);
        await uploadCollectionDocument(collection.id, file, setProgress);
      }
      await loadDocs();
      onChanged();
    } catch (err) {
      onError(err instanceof LibraryApiError ? err.message : "Yükleme başarısız.");
    } finally {
      setProgress(null);
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  const isStyle = collection.kind === "style-exemplar";

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02]">
      <div className="flex items-start gap-3 px-3.5 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13px] font-medium text-white">{collection.name}</span>
            <Chip tone={isStyle ? "amber" : "violet"}>
              {COLLECTION_KIND_LABELS[collection.kind]}
            </Chip>
            <Chip>{RETRIEVAL_MODE_LABELS[collection.retrievalMode]}</Chip>
            {!collection.isActive && <Chip tone="rose">pasif</Chip>}
          </div>
          {collection.description && (
            <p className="mt-1 text-[11.5px] leading-relaxed text-slate-500">
              {collection.description}
            </p>
          )}
          <p className="mt-1 text-[11px] text-slate-600">
            {collection.docCount} belge · {collection.chunkCount} parça
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Btn size="sm" tone="ghost" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Gizle" : "Belgeler"}
          </Btn>
          {!isStyle && collection.chunkCount > 0 && (
            <Btn size="sm" tone="ghost" onClick={() => setSearching((v) => !v)}>
              {searching ? "Aramayı Kapat" : "🔍 Ara"}
            </Btn>
          )}
          <Btn
            size="sm"
            tone="ghost"
            onClick={async () => {
              setBusy(true);
              try {
                await patchCollection(collection.id, { isActive: !collection.isActive });
                onChanged();
              } catch (err) {
                onError(err instanceof LibraryApiError ? err.message : "Güncellenemedi.");
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
          >
            {collection.isActive ? "Devre dışı" : "Etkinleştir"}
          </Btn>
          <Btn
            size="sm"
            tone="ghost"
            onClick={async () => {
              if (
                !confirm(
                  `"${collection.name}" bölümü ve içindeki ${collection.docCount} belge kalıcı olarak silinsin mi?`,
                )
              ) {
                return;
              }
              setBusy(true);
              try {
                await deleteCollection(collection.id);
                onChanged();
              } catch (err) {
                onError(err instanceof LibraryApiError ? err.message : "Silinemedi.");
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            className="hover:text-rose-400"
          >
            Sil
          </Btn>
        </div>
      </div>

      {searching && (
        <div className="border-t border-white/[0.06] px-3.5 py-3">
          <LibrarySearchPanel
            search={(q) => searchCollection(collection.id, q)}
            placeholder={`"${collection.name}" içinde ara…`}
          />
        </div>
      )}

      {expanded && (
        <div className="space-y-2 border-t border-white/[0.06] px-3.5 py-3">
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx,.rtf"
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <div className="flex items-center gap-2">
            <Btn size="sm" tone="outline" onClick={() => inputRef.current?.click()} disabled={busy}>
              + Belge Yükle
            </Btn>
            {progress !== null && (
              <span className="text-[11px] tabular-nums text-slate-500">%{progress}</span>
            )}
          </div>

          {docs.length === 0 ? (
            <p className="py-2 text-[11.5px] text-slate-600">
              {isStyle
                ? "Bu bölüme KENDİ yazdığın 3-8 belge yükle. Yazım tarzın buradan çıkarılacak."
                : "Henüz belge yok."}
            </p>
          ) : (
            <div className="space-y-1">
              {docs.map((d) => (
                <DocumentRow
                  key={d.id}
                  doc={d}
                  busy={busy}
                  onDelete={async () => {
                    setBusy(true);
                    try {
                      await deleteCollectionDocument(d.id);
                      await loadDocs();
                      onChanged();
                    } catch (err) {
                      onError(err instanceof LibraryApiError ? err.message : "Silinemedi.");
                    } finally {
                      setBusy(false);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StylePanel({
  personaId,
  style,
  hasStyleCollection,
  onRebuilt,
}: {
  personaId: string;
  style: StyleProfile;
  hasStyleCollection: boolean;
  onRebuilt: (next: StyleProfile) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function rebuild() {
    setBusy(true);
    setError(null);
    try {
      onRebuilt(await buildStyle(personaId));
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Stil çıkarılamadı.");
    } finally {
      setBusy(false);
    }
  }

  const spec = style.spec ?? {};
  const specRows: [string, string][] = [
    ["Ritim", spec.rhythm ?? ""],
    ["Hitap ve kayıt", spec.register ?? ""],
    ["Yapı", spec.structure ?? ""],
    ["Giriş", spec.openings ?? ""],
    ["Kapanış", spec.closings ?? ""],
    ["Noktalama", spec.punctuation ?? ""],
    ["Sözcük dağarcığı", spec.vocabulary ?? ""],
  ].filter((row): row is [string, string] => Boolean(row[1]));

  return (
    <div className="space-y-3">
      <SectionTitle
        title="Yazım Sesi"
        hint="'Stil Örneği' bölümüne yüklediğin belgelerden, yazım biçimini tarif eden mekanik bir kural seti çıkarılır ve personanın her turuna eklenir. İçerik değil, biçim öğrenilir."
        action={
          <Btn size="sm" tone="primary" onClick={rebuild} loading={busy} disabled={!hasStyleCollection}>
            {style.status === "ready" ? "Yeniden Çıkar" : "Stili Çıkar"}
          </Btn>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {!hasStyleCollection ? (
        <EmptyState
          title="Önce bir stil bölümü oluştur"
          description="Yukarıdan 'Stil Örneği' türünde bir hafıza bölümü ekle ve kendi yazdığın 3-8 belgeyi oraya yükle. Çeşitlilik hacimden önemlidir — bir e-posta ve bir rapor, altı rapordan daha çok şey anlatır."
        />
      ) : style.status !== "ready" ? (
        <EmptyState
          title="Stil henüz çıkarılmadı"
          description={
            style.error ??
            "Belgeler hazır. 'Stili Çıkar' ile analizi başlat — ölçümler yerel hesaplanır, kural setini model yazar."
          }
        />
      ) : (
        <div className="space-y-3">
          <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5">
            <div className="mb-2 flex items-center gap-1.5">
              <Chip tone="green">hazır</Chip>
              <span className="text-[11px] text-slate-500">
                {style.sampleCount} örnek parçadan çıkarıldı
              </span>
            </div>
            <dl className="space-y-1.5">
              {specRows.map(([label, value]) => (
                <div key={label} className="flex gap-2">
                  <dt className="w-32 shrink-0 text-[11.5px] text-slate-500">{label}</dt>
                  <dd className="flex-1 text-[12px] leading-relaxed text-slate-200">{value}</dd>
                </div>
              ))}
            </dl>
            {spec.signatureMoves && spec.signatureMoves.length > 0 && (
              <div className="mt-2 border-t border-white/[0.06] pt-2">
                <div className="mb-1 text-[11.5px] text-slate-500">İmza hamleler</div>
                <ul className="space-y-0.5">
                  {spec.signatureMoves.map((m) => (
                    <li key={m} className="text-[12px] text-slate-200">
                      · {m}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {style.bannedPhrases && style.bannedPhrases.length > 0 && (
              <div className="mt-2 border-t border-white/[0.06] pt-2">
                <div className="mb-1 text-[11.5px] text-slate-500">
                  Asla kullanılmayacak kalıplar
                </div>
                <div className="flex flex-wrap gap-1">
                  {style.bannedPhrases.map((p) => (
                    <Chip key={p} tone="rose">
                      {p}
                    </Chip>
                  ))}
                </div>
              </div>
            )}
          </div>

          {style.exemplars.length > 0 && (
            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5">
              <div className="mb-2 text-[12px] font-medium text-slate-300">Örnek pasajlar</div>
              <div className="space-y-2.5">
                {style.exemplars.map((ex, i) => (
                  <figure key={i} className="border-l-2 border-violet-500/40 pl-3">
                    <blockquote className="text-[12px] italic leading-relaxed text-slate-300">
                      “{ex.excerpt}”
                    </blockquote>
                    {ex.teachingNote && (
                      <figcaption className="mt-1 text-[11px] text-violet-300/80">
                        → {ex.teachingNote}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </div>
          )}

          {style.metrics && Object.keys(style.metrics).length > 0 && (
            <details className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5">
              <summary className="cursor-pointer text-[12px] font-medium text-slate-300">
                Ölçümler
              </summary>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                Bu sayılar modelin tahmini değil; metninizden doğrudan hesaplandı ve stil
                kurallarını üretirken modele girdi olarak verildi.
              </p>
              <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                {Object.entries(style.metrics)
                  .filter(([k]) => k in STYLE_METRIC_LABELS)
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2">
                      <dt className="text-[11px] text-slate-500">{STYLE_METRIC_LABELS[k]}</dt>
                      <dd className="text-[11px] tabular-nums text-slate-300">{v}</dd>
                    </div>
                  ))}
              </dl>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

export function ArchiveTab({
  personaId,
  collections,
  inherited,
  style,
  onCollectionsChanged,
  onStyleChanged,
}: {
  personaId: string;
  collections: MemoryCollection[];
  /** Bağlı olduğu birimden ve atalarından gelen bölümler — salt okunur. */
  inherited: MemoryCollection[];
  style: StyleProfile;
  onCollectionsChanged: (next: MemoryCollection[]) => void;
  onStyleChanged: (next: StyleProfile) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    try {
      onCollectionsChanged(await listCollections(personaId));
    } catch (err) {
      setError(err instanceof LibraryApiError ? err.message : "Bölümler yüklenemedi.");
    }
  }, [personaId, onCollectionsChanged]);

  const hasStyleCollection = collections.some((c) => c.kind === "style-exemplar");
  const hasSearchableContent =
    collections.some((c) => c.kind !== "style-exemplar" && c.chunkCount > 0) ||
    inherited.some((c) => c.chunkCount > 0);

  return (
    <div className="space-y-5">
      {hasSearchableContent && (
        <div className="space-y-2 rounded-xl border border-white/[0.07] bg-white/[0.015] p-3.5">
          <SectionTitle
            title="Tüm Arşivde Ara"
            hint="Bu personanın gördüğü HER ŞEYDE — kendi bölümleri + bağlı olduğu birimden miras aldıkları — anlamsal arama."
          />
          <LibrarySearchPanel search={(q) => searchPersona(personaId, q)} />
        </div>
      )}

      <div className="space-y-3">
        <SectionTitle
          title="Hafıza Bölümleri"
          hint="Personanın kişisel arşivi. Bir tartışmada soruyla ilgili olan parçalar prompt'una eklenir ve kaynağını belirterek alıntılar."
          action={
            <Btn size="sm" tone="outline" onClick={() => setCreating(true)}>
              + Bölüm Ekle
            </Btn>
          }
        />

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {collections.length === 0 ? (
          <EmptyState
            title="Henüz bölüm yok"
            description="Bir insanı kopyalar gibi düşün: 'Hazırladığım Evraklar', 'Sektör Raporları', 'İç Prosedürler' gibi bölümler açıp ilgili belgeleri yükle."
          />
        ) : (
          <div className="space-y-2">
            {collections.map((c) => (
              <CollectionCard
                key={c.id}
                collection={c}
                onChanged={() => void refresh()}
                onError={setError}
              />
            ))}
          </div>
        )}
      </div>

      {inherited.length > 0 && (
        <div className="space-y-2 border-t border-white/[0.07] pt-5">
          <SectionTitle
            title="Birimden Miras Alınan Belgeler"
            hint="Bağlı olduğu birimin ve üst birimlerinin belgeleri bu personaya otomatik açıktır. Düzenleme kaynağında yapılır — kopya tutmuyoruz, böylece 'hangisi güncel?' sorusu hiç doğmuyor."
          />
          <div className="space-y-1.5">
            {inherited.map((c) => (
              <div
                key={c.id}
                className="flex flex-wrap items-center gap-2 rounded-lg border border-white/[0.06] bg-white/[0.015] px-3 py-2"
              >
                <span className="text-[12px] text-slate-300">{c.name}</span>
                <Chip tone="cyan">birim</Chip>
                <Chip>{COLLECTION_KIND_LABELS[c.kind]}</Chip>
                <span className="ml-auto text-[10.5px] text-slate-600">
                  {c.docCount} belge · {c.chunkCount} parça
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-white/[0.07] pt-5">
        <StylePanel
          personaId={personaId}
          style={style}
          hasStyleCollection={hasStyleCollection}
          onRebuilt={onStyleChanged}
        />
      </div>

      <NewCollectionModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={async (payload) => {
          try {
            await createCollection(personaId, payload);
            await refresh();
            setCreating(false);
          } catch (err) {
            setError(err instanceof LibraryApiError ? err.message : "Bölüm oluşturulamadı.");
          }
        }}
      />
    </div>
  );
}

export function NewCollectionModal({
  open,
  onClose,
  onCreate,
  allowStyle = true,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: {
    name: string;
    description: string;
    kind: CollectionKind;
    retrievalMode: RetrievalMode;
    topK: number;
  }) => Promise<void>;
  /**
   * Birim bölümlerinde `false`. Yazım sesi KİŞİSELdir; bir departmana
   * "stil örneği" yüklemek altındaki herkesin sesini tek bir ortalamaya
   * indirger, o yüzden seçenek hiç sunulmuyor.
   */
  allowStyle?: boolean;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState<CollectionKind>("knowledge");
  const [mode, setMode] = useState<RetrievalMode>("on-demand");
  const [topK, setTopK] = useState(4);
  const [busy, setBusy] = useState(false);

  const isStyle = kind === "style-exemplar";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Yeni Hafıza Bölümü"
      subtitle="Belgeleri konusuna göre ayır; her bölümün geri getirme davranışı ayrı ayarlanır."
      width="max-w-xl"
      footer={
        <>
          <Btn tone="ghost" onClick={onClose} disabled={busy}>
            Vazgeç
          </Btn>
          <Btn
            tone="primary"
            disabled={name.trim().length < 1}
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onCreate({
                  name: name.trim(),
                  description: description.trim(),
                  kind,
                  retrievalMode: isStyle ? "style-only" : mode,
                  topK,
                });
                setName("");
                setDescription("");
              } finally {
                setBusy(false);
              }
            }}
          >
            Oluştur
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Bölüm Adı" required>
          <TextInput
            value={name}
            autoFocus
            maxLength={80}
            placeholder="ör. Hazırladığım Evraklar"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field label="Tür" hint={COLLECTION_KIND_HINTS[kind]}>
          <Select value={kind} onChange={(e) => setKind(e.target.value as CollectionKind)}>
            {(Object.keys(COLLECTION_KIND_LABELS) as CollectionKind[])
              .filter((k) => allowStyle || k !== "style-exemplar")
              .map((k) => (
                <option key={k} value={k}>
                  {COLLECTION_KIND_LABELS[k]}
                </option>
              ))}
          </Select>
        </Field>

        {isStyle && (
          <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2 text-[11.5px] leading-relaxed text-amber-200">
            Bu bölümdeki belgeler tartışmada <strong>alıntılanmaz</strong>. Yalnızca yazım
            tarzınız (cümle ritmi, hitap, yapı, kaçındığınız kalıplar) çıkarılır ve personanın
            yazım sesine dönüştürülür.
          </div>
        )}

        <Field label="Açıklama">
          <TextArea
            rows={2}
            value={description}
            maxLength={600}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>

        {!isStyle && (
          <>
            <Field label="Geri Getirme">
              <Select value={mode} onChange={(e) => setMode(e.target.value as RetrievalMode)}>
                <option value="on-demand">{RETRIEVAL_MODE_LABELS["on-demand"]}</option>
                <option value="always">{RETRIEVAL_MODE_LABELS.always}</option>
              </Select>
            </Field>
            <Field label={`Tur başına en fazla ${topK} parça`}>
              <input
                type="range"
                min={1}
                max={10}
                value={topK}
                onChange={(e) => setTopK(Number(e.target.value))}
                className="w-full accent-violet-500"
              />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}